// A device's configuration on its page in Operate, in one section (#1801):
// what the controller holds against what the device reports running, then the
// switch's own running configuration read over its console, then — for
// provider staff — the rest of what the controller holds.
//
// **It replaces three sections that said one fact three times.** The head's
// Running pill, *Compared*'s Held and Running, and Diagnostics' *What the
// controller holds* each drew the same generation pair, from the same cell
// read. The head keeps its pill — the state word, `runningOf`'s — and this
// section draws the two numbers once. Compared's ports block was a plan's, and
// a plan is made on Design › Wired, so it stays there (`Compared.tsx` is still
// that tab's); this page made none and said so.
//
// **Held and running stay two words** (ADR-0078). The first is the
// controller's copy and the second is the device's own report, and two
// generation numbers that agree are not two configurations that agree: a
// change made at a switch's console moves no generation. The field-level
// comparison is the console read below it (ADR-0115 decision 6), which is the
// only thing on this page that earns *running* for a field.
//
// Nothing here is a new read. The pair is a switch's own device read and an
// access point's site convergence read, both the page's; the console read is
// `RunningConfigRead`'s, under its own capability; the provider's part is the
// diagnostics read the Diagnostics tab makes, made here only when a superuser
// opens it.
import { lazy, Suspense, useState } from "react";
import { Card, Info, Loading, Pill } from "../../ui";
import type { Device } from "./commands";
import type { useConvergence } from "./DevicesTable";
import RunningConfigRead from "./RunningConfigRead";

// Provider staff only, and in the Diagnostics chunk with the rest of its read.
const ControllerHolds = lazy(() => import("./Diagnostics").then((m) => ({ default: m.ControllerHolds })));

/** A number, or why there is none. */
function Gen({ value }: { value?: number }) {
  return value === undefined ? <span className="muted">not reported</span> : <code>{value}</code>;
}

/** The two numbers, each named by whose it is. */
function Pair({ held, running, whose }: { held?: number; running?: number; whose: string }) {
  return (
    <>
      {/* Restates ADR-0022 decision 3, ZENITH.md §11.2 — the held generation is the
          controller's record of what it means the device to run, and is a different
          fact from what the device reports. Moved here from Diagnostics (#1801). */}
      <span title="the generation the controller holds for this device">Held <Gen value={held} /> <span className="dim">by the controller</span></span>
      {" · "}
      {/* Restates ADR-0099, ADR-0108, ADR-0111, ADR-0140, ADR-0449, ADR-0022 decision 3 — the title says where
          Zenith read this, not that the number is the device's answer. Zenith takes it from the
          status record and never from the held row; with a push outstanding the controller has been
          measured answering it with the generation it is still pushing; where the controller holds
          no record there is no number to show; where the device's own last report declares a
          generation the controller does not hold, that declaration is read in its place; and for a
          switch whose controller holds only a stub the status record's number is one it never
          assigned, so the switch's own stored declaration is read where there is one and nothing is
          shown while it is not online (ADR-0449). Moved here from Diagnostics with its sentence (#1801); the pair
          is drawn once on the page. */}
      <span title="the generation the controller's status record reports this device running — read from there and never from the held row, though with a push pending it may be the generation being pushed rather than the device's own answer, and absent entirely where the controller holds no record to have read one from; where the device's own last report declares a generation the controller does not hold, that declaration is read in its place; and for a switch whose controller holds only a stub, whose status record carries a number it never assigned, the switch's own last report is read where the controller has stored one, and nothing is shown while the switch is not online">Running <Gen value={running} /> <span className="dim">{whose}</span></span>
    </>
  );
}

/** A switch's line, from its own device read: the pair and whether the two
 * numbers differ — never that the two configurations match. */
function SwitchLine({ read, status }: { read?: Device; status: string }) {
  if (status === "loading") return <Loading label="Loading the switch" />;
  const g = read?.configuration_generations;
  if (!g) return <p className="muted">The switch&rsquo;s generations were not read{status === "error" ? ": the device read failed" : ""}.</p>;
  return (
    <p className="config-line">
      {/* Restates ADR-0074 decision 1, ADR-0078 — the two numbers are held and running
          and the words say which is which; a switch is judged against the generation
          its own controller holds. */}
      <Pair held={g.held} running={g.running} whose="as the switch reports it" />
      {" · "}
      {/* Restates ADR-0078 — agreement between two generations is not agreement between
          two configurations: a change made at the switch's console moves no generation,
          so this may never be rendered as the documents matching. */}
      {g.differs
        ? <Pill tone="warn" title="The switch reports running a different generation from the one the controller holds.">differs</Pill>
        : g.held !== undefined && g.running !== undefined
          ? <span title={"Two generation numbers agree. That is not the switch’s configuration agreeing with the controller’s copy: a change made at the switch by hand moves no generation."}>the switch reports the generation its controller holds</span>
          : <span className="muted">not both reported</span>}
      {/* The read's own detail is why, not what (UX-012, the 2026-11-01 review):
          the line is the state, and the explanation sits behind the ⓘ beside it. */}
      {g.detail && <Info label="About this reading">{g.detail}</Info>}
    </p>
  );
}

/** An access point's line, from the site's convergence read. The state word is
 * the head's Running pill and is not drawn a second time here; the read's own
 * `detail` is what this adds. */
function AccessPointLine({ serial, convergence }: { serial: string; convergence?: ReturnType<typeof useConvergence> }) {
  if (!convergence || convergence.status === "loading") return <Loading label="Loading convergence" />;
  if (convergence.status === "error") return <p className="muted">Convergence could not be read: {convergence.problem.message}</p>;
  if (convergence.status !== "ready") return <p className="muted">Convergence was not read.</p>;
  const d = convergence.data.devices.find((x) => x.serial === serial);
  if (!d) {
    const ex = convergence.data.excluded.find((x) => x.serial === serial);
    return <p className="muted">Not judged{ex ? <>: {ex.reason}</> : convergence.data.reason ? <>: {convergence.data.reason === "placement" ? "the site's placement does not resolve" : "the site has no venue"}</> : null}.</p>;
  }
  return (
    <p className="config-line">
      <Pair held={d.held} running={d.running} whose="as the access point reports it" />
      {/* The convergence read's detail is its reasoning, often several sentences
          (UX-012): the state word is the head's pill, and the reason is behind the ⓘ. */}
      {d.detail && <Info label="About this reading">{d.detail}</Info>}
    </p>
  );
}

export default function Configuration({ device, read, readStatus, convergence, mayReadRunning = false, mayDiagnose = false }: {
  device: Device;
  /** The device's own read, which carries a switch's generation pair (#421). */
  read?: Device;
  readStatus: string;
  /** The site's convergence read, the page's own: an access point's pair. */
  convergence?: ReturnType<typeof useConvergence>;
  /** `device.cli.read` at the site: whether a switch's running configuration can be read (ADR-0115). */
  mayReadRunning?: boolean;
  /** The superuser flag (ADR-0092 decision 4): whether the provider's part is offered at all. */
  mayDiagnose?: boolean;
}) {
  const isSwitch = device.category === "switch";
  // Opened by hand, so the provider's read reaches the cell only when asked for.
  const [holds, setHolds] = useState(false);
  return (
    <Card className="panel" title="Configuration">
      {isSwitch ? <SwitchLine read={read} status={readStatus} /> : <AccessPointLine serial={device.serial} convergence={convergence} />}
      {isSwitch && <RunningConfigRead device={device} mayRead={mayReadRunning} embedded />}
      {mayDiagnose && (
        <div className="config-holds">
          <button type="button" className="btn btn-quiet" aria-expanded={holds} onClick={() => setHolds((o) => !o)}>
            What the controller holds<span className="dim"> · provider staff</span>
          </button>
          {holds && (
            <Suspense fallback={<Loading label="Loading what the controller holds" />}>
              <ControllerHolds serial={device.serial} />
            </Suspense>
          )}
        </div>
      )}
    </Card>
  );
}
